import { extractAddressFromGoogleResult } from '@my/ui';
import type { ImportDraft } from './types';

/** Patch a successful geocode contributes back to a draft. */
export type GeocodePatch = Partial<
  Pick<ImportDraft, 'placeId' | 'city' | 'postcode' | 'country' | 'latitude' | 'longitude'>
>;

/**
 * Resolve one draft against the browser's Google Geocoder (the SDK is already
 * loaded app-wide via `<APIProvider>`). Strategy:
 *   1. Forward-geocode the full address → place_id + components + coords.
 *   2. If that yields nothing, reverse-geocode the draft's existing lat/lng.
 *
 * Fill semantics: `placeId` is always set (it starts empty); city / postcode /
 * country / lat / lng are **fill-if-blank** — operator- or file-supplied
 * values win, so a verified coordinate from the source file is never
 * clobbered. Returns `null` when nothing resolved.
 */
export async function geocodeDraft(
  geocoder: google.maps.Geocoder,
  draft: ImportDraft,
): Promise<GeocodePatch | null> {
  // 1. Forward.
  if (draft.fullAddress.trim()) {
    const first = await tryGeocode(geocoder, { address: draft.fullAddress.trim() });
    if (first) return toPatch(first, draft);
  }
  // 2. Reverse on existing coords.
  if (draft.latitude != null && draft.longitude != null) {
    const first = await tryGeocode(geocoder, {
      location: { lat: draft.latitude, lng: draft.longitude },
    });
    if (first) return toPatch(first, draft);
  }
  return null;
}

async function tryGeocode(
  geocoder: google.maps.Geocoder,
  request: google.maps.GeocoderRequest,
): Promise<google.maps.GeocoderResult | null> {
  try {
    const { results } = await geocoder.geocode(request);
    return results?.[0] ?? null;
  } catch {
    // ZERO_RESULTS / OVER_QUERY_LIMIT / transient — caller treats as unresolved.
    return null;
  }
}

function toPatch(result: google.maps.GeocoderResult, draft: ImportDraft): GeocodePatch {
  const x = extractAddressFromGoogleResult(result);
  const patch: GeocodePatch = {};

  if (x.placeId) patch.placeId = x.placeId; // always adopt — starts empty
  if (!draft.city.trim() && x.city) patch.city = x.city;
  if (!draft.postcode.trim() && x.postcode) patch.postcode = x.postcode;
  if (!draft.country.trim() && x.countryIsoCode) patch.country = x.countryIsoCode;
  // Coords: only fill when the draft has none (preserve source-file values).
  if (draft.latitude == null && x.latitude) patch.latitude = x.latitude;
  if (draft.longitude == null && x.longitude) patch.longitude = x.longitude;

  return patch;
}

/**
 * Build a Google Maps deep-link for the "view on map" affordance — prefers a
 * place_id, falls back to coordinates. Returns null when neither is available.
 */
export function googleMapsLink(draft: ImportDraft): string | null {
  if (draft.placeId) {
    return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(draft.placeId)}`;
  }
  if (draft.latitude != null && draft.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${draft.latitude},${draft.longitude}`;
  }
  return null;
}
